/**
 * QueryFilePreview Component
 *
 * Displays parsed query information from uploaded YAML file
 */

import React from 'react';
import type { QueryFile } from '@/types/connectors';

interface QueryFilePreviewProps {
  queryFile: QueryFile;
  maxVisible?: number;
}

export const QueryFilePreview: React.FC<QueryFilePreviewProps> = ({
  queryFile,
  maxVisible = 5,
}) => {
  const { queries } = queryFile;
  const visibleQueries = queries.slice(0, maxVisible);
  const remainingCount = queries.length - maxVisible;

  const isDatabaseConnector = queries.some((q) => q.type === 'table');

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        {isDatabaseConnector ? (
          <>
            <span className="font-medium">{queries.length}</span>{' '}
            {queries.length === 1 ? 'table' : 'tables'} configured
          </>
        ) : (
          <>
            <span className="font-medium">{queries.length}</span>{' '}
            {queries.length === 1 ? 'query' : 'queries'} found
          </>
        )}
      </p>

      <div className="space-y-1">
        <p className="text-sm font-medium">
          {isDatabaseConnector ? 'Tables:' : 'Queries:'}
        </p>
        <ul className="list-disc list-inside space-y-0.5 text-sm text-muted-foreground">
          {visibleQueries.map((query, index) => (
            <li key={index}>
              <span className="font-medium text-foreground">{query.name}</span>
              {query.columns && query.columns.length > 0 && (
                <span className="text-xs ml-1">
                  ({query.columns.slice(0, 2).join(', ')}
                  {query.columns.length > 2 && ', ...'})
                </span>
              )}
            </li>
          ))}
          {remainingCount > 0 && (
            <li className="text-muted-foreground">
              + {remainingCount} more
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};
